/**
 * Money helpers. The DB stores `INTEGER` cents; the UI works in dollars.
 * Always convert at the boundary — never let cents leak into form inputs or
 * dollars into INSERT statements.
 */

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const currencyCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format DB cents as a display string: 38900000 → "$389,000". */
export function formatMoney(cents: number | null | undefined, opts: { showCents?: boolean } = {}): string {
  if (cents === null || cents === undefined) return '—';
  const dollars = cents / 100;
  return opts.showCents ? currencyCents.format(dollars) : currency.format(dollars);
}

/** Strip everything but digits + decimal point, then parse to a number of dollars (not cents). */
export function parseMoneyInput(s: string): number | null {
  const cleaned = s.replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build 56 (Daniel 2026-09-23): "money or values use $1,000,000 type".
 *
 * Whole dollars, the way the team writes a price: $324,500. formatMoney above
 * takes CENTS (the deal ledger stores cents); this takes DOLLARS, which is
 * what contact home values, purchase prices and AVMs are stored in. Two
 * functions rather than one with a flag, because passing dollars to a cents
 * formatter prints $3,245 for a $324,500 house and nothing about the call
 * looks wrong.
 */
export function formatDollars(dollars: number | null | undefined, blank = ''): string {
  if (dollars === null || dollars === undefined || !Number.isFinite(dollars)) return blank;
  return currency.format(Math.round(dollars));
}

/** "324500" -> "324,500". Digits only in, grouped digits out. */
export function groupDigits(digits: string): string {
  const d = digits.replace(/^0+(?=\d)/, '');
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** A rate or percent for reading: 6.25 -> "6.25%", 4 -> "4%". */
export function formatRate(pct: number | null | undefined, blank = ''): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return blank;
  return `${Number(pct.toFixed(3))}%`;
}
